import React, { useState, useEffect, useCallback } from 'react';
import { useApp } from '../context/AppContext';
import api, { getCuentas } from '../services/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Building2, Upload, FileSpreadsheet, CheckCircle2, XCircle, AlertCircle, AlertTriangle,
  Link2, ArrowRightLeft, Search, Calendar, RefreshCw, Eye, Plus, Settings, Unlink
} from 'lucide-react';
import * as XLSX from 'xlsx';

// Formatos predefinidos para bancos peruanos
const FORMATOS_BANCO = {
  BCP: {
    nombre: 'BCP - Formato Estándar',
    skip_filas: 1,  // Saltar encabezado
    col_fecha: 'B',
    col_descripcion: 'D',
    col_monto: 'E',  // Monto único (positivo=ingreso, negativo=cargo)
    col_saldo: 'F',
    col_ref: 'H',  // Número de operación
  },
  BBVA: {
    nombre: 'BBVA - Formato Estándar',
    skip_filas: 1,  // Fila 0 es encabezado, fila 1 es saldo
    col_fecha: 'B',
    col_descripcion: 'F',  // Concepto
    col_monto: 'G',  // Importe (único)
    col_ref: 'E',  // Nº Doc
  },
  INTERBANK: {
    nombre: 'Interbank - Formato Estándar',
    skip_filas: 14,  // Muchas filas de cabecera, datos desde fila 14
    col_fecha: 'B',
    col_descripcion: 'F',  // Descripción
    col_cargo: 'H',  // Cargo (negativo)
    col_abono: 'I',  // Abono (positivo)
    col_saldo: 'J',
    col_ref: 'D',  // Nro operación
  },
  SCOTIABANK: {
    nombre: 'Scotiabank - Formato Estándar',
    skip_filas: 1,
    col_fecha: 'A',
    col_descripcion: 'B',
    col_monto: 'C',
    col_saldo: 'D',
  },
  CUSTOM: {
    nombre: 'Personalizado',
    skip_filas: 0,
    col_fecha: 'A',
    col_descripcion: 'B',
    col_monto: 'C',
  }
};

const ConciliacionPage = () => {
  const { empresaActual } = useApp();
  const [cuentas, setCuentas] = useState([]);
  const [cuentaId, setCuentaId] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Fechas
  const [fechaDesde, setFechaDesde] = useState(() => {
    const d = new Date();
    d.setDate(1);
    return d.toISOString().split('T')[0];
  });
  const [fechaHasta, setFechaHasta] = useState(() => new Date().toISOString().split('T')[0]);
  
  // Datos de conciliación
  const [movBanco, setMovBanco] = useState([]);  // Solo pendientes (para pestaña Pendientes)
  const [movBancoTodos, setMovBancoTodos] = useState([]);  // Todos (para pestaña Movimientos Banco)
  const [movSistema, setMovSistema] = useState([]);
  const [resumen, setResumen] = useState({ banco_count: 0, banco_total: 0, sistema_count: 0, sistema_total: 0 });
  const [historialConciliados, setHistorialConciliados] = useState([]);
  
  // Modal importar
  const [modalImportOpen, setModalImportOpen] = useState(false);
  const [bancoSeleccionado, setBancoSeleccionado] = useState('BCP');
  const [formatoConfig, setFormatoConfig] = useState({...FORMATOS_BANCO.BCP});
  const [archivoData, setArchivoData] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Modal conciliar
  const [modalConciliarOpen, setModalConciliarOpen] = useState(false);
  const [movBancoSeleccionado, setMovBancoSeleccionado] = useState(null);
  const [movBancoMultiples, setMovBancoMultiples] = useState([]); // Para selección múltiple
  const [pagosSeleccionados, setPagosSeleccionados] = useState([]);
  const [notaConciliacion, setNotaConciliacion] = useState('');
  
  // Opciones de gasto
  const [crearGasto, setCrearGasto] = useState(false);
  const [categoriaGasto, setCategoriaGasto] = useState('ITF');
  const [descripcionGasto, setDescripcionGasto] = useState('');
  const [centroCostoGasto, setCentroCostoGasto] = useState('none');
  
  // Tab activa
  const [tabActiva, setTabActiva] = useState('pendientes');
  
  // Centros de costo
  const [centrosCosto, setCentrosCosto] = useState([]);
  
  // Modal confirmar romper conciliación
  const [alertRomperOpen, setAlertRomperOpen] = useState(false);
  const [conciliacionARomper, setConciliacionARomper] = useState(null);
  
  // Modal confirmar diferencia de montos
  const [confirmacionDiferencia, setConfirmacionDiferencia] = useState(null);
  
  // Ordenamiento historial
  const [sortColumn, setSortColumn] = useState('fecha_mov');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Anchos de columnas (redimensionables)
  const [columnWidths, setColumnWidths] = useState({
    fecha: 100,
    banco: 80,
    nro_op: 90,
    desc_banco: 150,
    monto_banco: 110,
    nro_doc: 100,
    proveedor: 150,
    detalle: 120,
    desc_sistema: 150,
    monto_sistema: 110,
    estado: 80,
  });

  // Cargar cuentas
  useEffect(() => {
    if (empresaActual?.id) {
      getCuentas(empresaActual.id).then(res => {
        const cuentasBanco = res.data.filter(c => c.tipo === 'BANCO');
        setCuentas(cuentasBanco);
        if (cuentasBanco.length > 0 && !cuentaId) {
          setCuentaId(cuentasBanco[0].id.toString());
        }
      });
      // Cargar centros de costo
      api.get(`/centros-costo?empresa_id=${empresaActual.id}`).then(res => {
        setCentrosCosto(res.data);
      }).catch(() => {});
    }
  }, [empresaActual?.id]);

  // Cargar datos de conciliación
  const cargarDatos = useCallback(async () => {
    if (!empresaActual?.id || !cuentaId) return;
    
    setLoading(true);
    try {
      const [pendientesRes, historialRes, todosMovRes] = await Promise.all([
        api.get('/conciliacion/pendientes', {
          params: { 
            empresa_id: empresaActual.id, 
            cuenta_id: cuentaId,
            fecha_desde: fechaDesde,
            fecha_hasta: fechaHasta,
          }
        }),
        api.get('/conciliacion/historial', {
          params: { 
            empresa_id: empresaActual.id, 
            cuenta_id: cuentaId,
          }
        }),
        api.get('/banco-movimientos', {
          params: { 
            empresa_id: empresaActual.id, 
            cuenta_id: cuentaId,
          }
        })
      ]);
      // Limpiar datos para evitar referencias circulares
      const cleanBanco = (pendientesRes.data.banco || []).map(m => ({
        id: m.id,
        fecha: m.fecha,
        banco: m.banco,
        ref_banco: m.ref_banco,
        descripcion: m.descripcion,
        monto_pen: m.monto_pen,
        monto_me: m.monto_me,
        tipo: m.tipo,
        cuenta_id: m.cuenta_id,
        conciliado: m.conciliado
      }));
      setMovBanco(cleanBanco);
      setMovSistema(pendientesRes.data.sistema || []);
      setResumen(pendientesRes.data.resumen);
      setHistorialConciliados(historialRes.data);
      // Limpiar todos los movimientos
      const cleanTodos = (todosMovRes.data || []).map(m => ({
        id: m.id,
        fecha: m.fecha,
        banco: m.banco,
        ref_banco: m.ref_banco,
        descripcion: m.descripcion,
        monto_pen: m.monto_pen,
        monto_me: m.monto_me,
        tipo: m.tipo,
        cuenta_id: m.cuenta_id,
        conciliado: m.conciliado
      }));
      setMovBancoTodos(cleanTodos);
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  }, [empresaActual?.id, cuentaId, fechaDesde, fechaHasta]);

  useEffect(() => {
    if (cuentaId) cargarDatos();
  }, [cargarDatos, cuentaId]);

  // Manejar cambio de banco en importación
  const handleBancoChange = (banco) => {
    setBancoSeleccionado(banco);
    setFormatoConfig({ ...FORMATOS_BANCO[banco] });
    setPreviewData([]);
  };

  // Parsear archivo Excel
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) {
      console.log('No se seleccionó archivo');
      return;
    }
    
    console.log('Archivo seleccionado:', file.name, file.size, 'bytes');
    
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        console.log('Leyendo archivo...');
        const workbook = XLSX.read(event.target.result, { type: 'binary', cellDates: true });
        const sheetName = workbook.SheetNames[0];
        console.log('Hoja:', sheetName);
        const sheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(sheet, { header: 'A', raw: false, defval: '' });
        
        console.log('Datos leídos:', jsonData.length, 'filas');
        console.log('Primera fila:', jsonData[0]);
        console.log('Segunda fila:', jsonData[1]);
        
        setArchivoData(jsonData);
        
        // Preview con formato actual
        parsePreview(jsonData, formatoConfig);
      } catch (error) {
        toast.error('Error al leer archivo Excel: ' + error.message);
        console.error('Error leyendo Excel:', error);
      }
    };
    reader.onerror = (error) => {
      console.error('Error FileReader:', error);
      toast.error('Error al leer el archivo');
    };
    reader.readAsBinaryString(file);
  };

  // Parsear datos para preview según formato
  const parsePreview = (data, formato) => {
    if (!data || data.length === 0) {
      console.log('No hay datos para parsear');
      return;
    }
    
    console.log('Parseando datos:', data.length, 'filas');
    console.log('Formato:', formato);
    console.log('Primera fila:', data[0]);
    
    const startRow = formato.skip_filas || 0;
    const parsed = [];
    
    for (let i = startRow; i < Math.min(data.length, startRow + 200); i++) {
      const row = data[i];
      if (!row) continue;
      
      // Obtener valores según columnas configuradas
      const fechaRaw = row[formato.col_fecha] || '';
      const descripcion = row[formato.col_descripcion] || '';
      const ref = formato.col_ref ? (row[formato.col_ref] || '') : '';
      const saldo = formato.col_saldo ? parseFloat(row[formato.col_saldo]?.toString().replace(/[^0-9.-]/g, '') || 0) : null;
      
      // Filtrar filas que son solo resumen (BBVA tiene "Saldo Final" en algunas filas)
      if (descripcion.toString().toLowerCase().includes('saldo final')) {
        continue;
      }
      
      // Monto: puede ser una columna o cargo/abono separados
      let monto = 0;
      if (formato.col_monto) {
        monto = parseFloat(row[formato.col_monto]?.toString().replace(/[^0-9.-]/g, '') || 0);
      } else if (formato.col_cargo || formato.col_abono) {
        const cargoRaw = formato.col_cargo ? row[formato.col_cargo] : null;
        const abonoRaw = formato.col_abono ? row[formato.col_abono] : null;
        const cargo = cargoRaw ? Math.abs(parseFloat(cargoRaw.toString().replace(/[^0-9.-]/g, '') || 0)) : 0;
        const abono = abonoRaw ? Math.abs(parseFloat(abonoRaw.toString().replace(/[^0-9.-]/g, '') || 0)) : 0;
        monto = abono > 0 ? abono : (cargo > 0 ? -cargo : 0);
      }
      
      // Parsear fecha - más tolerante
      let fecha = '';
      if (fechaRaw) {
        if (fechaRaw instanceof Date) {
          fecha = fechaRaw.toISOString().split('T')[0];
        } else {
          const dateStr = fechaRaw.toString().trim();
          // Formato DD/MM/YYYY
          if (dateStr.match(/^\d{1,2}\/\d{1,2}\/\d{2,4}$/)) {
            const parts = dateStr.split('/');
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            let year = parts[2];
            if (year.length === 2) year = '20' + year;
            fecha = `${year}-${month}-${day}`;
          }
          // Formato YYYY-MM-DD
          else if (dateStr.match(/^\d{4}-\d{2}-\d{2}$/)) {
            fecha = dateStr;
          }
          // Formato DD-MM-YYYY
          else if (dateStr.match(/^\d{1,2}-\d{1,2}-\d{2,4}$/)) {
            const parts = dateStr.split('-');
            const day = parts[0].padStart(2, '0');
            const month = parts[1].padStart(2, '0');
            let year = parts[2];
            if (year.length === 2) year = '20' + year;
            fecha = `${year}-${month}-${day}`;
          }
          // Si hay número de Excel (días desde 1900)
          else if (!isNaN(parseFloat(dateStr)) && parseFloat(dateStr) > 30000) {
            const excelDate = new Date((parseFloat(dateStr) - 25569) * 86400 * 1000);
            fecha = excelDate.toISOString().split('T')[0];
          }
        }
      }
      
      // Log para debug
      if (i < startRow + 3) {
        console.log(`Fila ${i}:`, { fechaRaw, fecha, descripcion, monto });
      }
      
      // Agregar filas con datos - más tolerante (no requerir fecha válida si hay descripción)
      if ((fecha || descripcion) && (monto !== 0 || descripcion)) {
        parsed.push({ 
          fecha: fecha || new Date().toISOString().split('T')[0], // Usar hoy si no hay fecha
          descripcion: descripcion || 'Sin descripción', 
          ref, 
          monto, 
          saldo 
        });
      }
    }
    
    console.log('Movimientos parseados:', parsed.length);
    setPreviewData(parsed);
  };

  // Re-parsear cuando cambia formato
  useEffect(() => {
    if (archivoData) {
      parsePreview(archivoData, formatoConfig);
    }
  }, [formatoConfig]);

  // Importar movimientos
  const handleImportar = async () => {
    console.log('handleImportar llamado, previewData:', previewData.length);
    
    if (previewData.length === 0) {
      toast.error('No hay datos para importar');
      return;
    }
    
    setImporting(true);
    try {
      const cuenta = cuentas.find(c => c.id.toString() === cuentaId);
      console.log('Enviando a API:', { empresa_id: empresaActual.id, cuenta_id: cuentaId, banco: bancoSeleccionado, movimientos: previewData.length });
      
      const response = await api.post('/banco-movimientos/importar', {
        empresa_id: empresaActual.id,
        cuenta_id: parseInt(cuentaId),
        moneda_id: cuenta?.moneda_id || 1,
        banco: bancoSeleccionado,  // Agregar nombre del banco
        movimientos: previewData,
      });
      
      console.log('Respuesta:', response.data);
      const { importados, actualizados, errores } = response.data;
      let mensaje = `Importación completada: ${importados} nuevos`;
      if (actualizados > 0) mensaje += `, ${actualizados} actualizados`;
      if (errores > 0) mensaje += `, ${errores} errores`;
      toast.success(mensaje);
      setModalImportOpen(false);
      setArchivoData(null);
      setPreviewData([]);
      cargarDatos();
    } catch (error) {
      console.error('Error al importar:', error);
      toast.error('Error al importar: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  // Abrir modal de conciliación (un solo movimiento)
  const handleAbrirConciliar = (movBanco) => {
    // Crear copia limpia del objeto
    const movClean = {
      id: movBanco.id,
      fecha: movBanco.fecha,
      banco: movBanco.banco,
      ref_banco: movBanco.ref_banco,
      descripcion: movBanco.descripcion,
      monto_pen: movBanco.monto_pen,
      tipo: movBanco.tipo,
      cuenta_id: movBanco.cuenta_id
    };
    
    setMovBancoSeleccionado(movClean);
    setMovBancoMultiples([movClean]);
    setPagosSeleccionados([]);
    setNotaConciliacion('');
    setCrearGasto(false);
    setCategoriaGasto('ITF');
    setDescripcionGasto('');
    setCentroCostoGasto('none');
    setModalConciliarOpen(true);
  };

  // Toggle selección de movimiento banco para conciliación múltiple
  const toggleMovBancoSeleccionado = (mov) => {
    // Crear copia limpia del objeto para evitar referencias circulares
    const movClean = {
      id: mov.id,
      fecha: mov.fecha,
      banco: mov.banco,
      ref_banco: mov.ref_banco,
      descripcion: mov.descripcion,
      monto_pen: mov.monto_pen,
      tipo: mov.tipo,
      cuenta_id: mov.cuenta_id
    };
    
    setMovBancoMultiples(prev => {
      const exists = prev.find(m => m.id === mov.id);
      if (exists) {
        return prev.filter(m => m.id !== mov.id);
      } else {
        return [...prev, movClean];
      }
    });
  };

  // Abrir modal con múltiples movimientos seleccionados
  const handleAbrirConciliarMultiples = () => {
    if (movBancoMultiples.length === 0) {
      toast.error('Selecciona al menos un movimiento');
      return;
    }
    setMovBancoSeleccionado(movBancoMultiples[0]);
    setPagosSeleccionados([]);
    setNotaConciliacion('');
    setCrearGasto(true); // Por defecto crear gasto cuando es múltiple
    setCategoriaGasto('ITF');
    setDescripcionGasto('');
    setCentroCostoGasto('none');
    setModalConciliarOpen(true);
  };

  // Toggle selección de pago
  const togglePagoSeleccionado = (pagoId) => {
    setPagosSeleccionados(prev => 
      prev.includes(pagoId) ? prev.filter(id => id !== pagoId) : [...prev, pagoId]
    );
  };

  // Ejecutar conciliación
  const handleConciliar = async (forzar = false) => {
    if (movBancoMultiples.length === 0) return;
    
    try {
      // Extraer solo los IDs para evitar referencias circulares
      const bancoMovIds = movBancoMultiples.map(m => m.id);
      const ccId = centroCostoGasto === 'none' ? null : parseInt(centroCostoGasto);
      const empId = empresaActual?.id;
      
      if (!empId) {
        toast.error('Selecciona una empresa');
        return;
      }
      
      if (pagosSeleccionados.length === 0) {
        // Conciliar sin contrapartida - usar endpoint de múltiples
        const payload = {
          empresa_id: empId,
          banco_mov_ids: bancoMovIds,
          crear_gasto: Boolean(crearGasto),
          categoria_gasto: String(categoriaGasto || 'ITF'),
          descripcion_gasto: String(descripcionGasto || ''),
          centro_costo_id: ccId,
          nota: String(notaConciliacion || ''),
        };
        
        const response = await api.post('/conciliacion/conciliar-multiples', payload);
        
        if (crearGasto) {
          toast.success(`${response.data.movimientos_conciliados} movimientos conciliados y gasto creado por S/. ${formatMonto(response.data.total_monto)}`);
        } else {
          toast.success(`${response.data.movimientos_conciliados} movimientos marcados como conciliados`);
        }
      } else {
        // Conciliar con pagos del sistema (solo un movimiento banco)
        const payload = {
          empresa_id: empId,
          banco_mov_id: bancoMovIds[0],
          pago_ids: [...pagosSeleccionados],
          nota: String(notaConciliacion || ''),
          forzar: Boolean(forzar),
        };
        
        await api.post('/conciliacion/conciliar', payload);
        toast.success('Conciliación realizada correctamente');
      }
      
      setModalConciliarOpen(false);
      setMovBancoMultiples([]);
      setConfirmacionDiferencia(null);
      cargarDatos();
    } catch (error) {
      // Verificar si es un error de montos no coincidentes
      const detail = error.response?.data?.detail;
      if (detail && detail.requiere_confirmacion) {
        // Mostrar diálogo de confirmación con la diferencia
        setConfirmacionDiferencia({
          monto_banco: detail.monto_banco,
          monto_sistema: detail.monto_sistema,
          diferencia: detail.diferencia,
        });
      } else {
        toast.error('Error al conciliar: ' + (typeof detail === 'string' ? detail : detail?.message || error.message));
      }
    }
  };

  // Conciliar automáticamente por monto exacto
  const handleConciliarAuto = async () => {
    let conciliados = 0;
    
    for (const movB of movBanco) {
      // Buscar pago del sistema con mismo monto (aproximado)
      const pagoMatch = movSistema.find(movS => {
        const diff = Math.abs(Math.abs(movB.monto_pen) - Math.abs(movS.monto_pen));
        return diff < 0.01 && !pagosSeleccionados.includes(movS.id);
      });
      
      if (pagoMatch) {
        try {
          await api.post('/conciliacion/conciliar', {
            empresa_id: empresaActual.id,
            banco_mov_id: movB.id,
            pago_ids: [pagoMatch.id],
            nota: 'Conciliación automática por monto',
          });
          conciliados++;
        } catch (e) {
          // Ignorar errores individuales
        }
      }
    }
    
    toast.success(`Se conciliaron ${conciliados} movimientos automáticamente`);
    cargarDatos();
  };

  // Calcular total de movimientos seleccionados
  const totalSeleccionados = movBancoMultiples.reduce((sum, m) => sum + (parseFloat(m.monto_pen) || 0), 0);

  // Romper conciliación - abrir diálogo de confirmación
  const handleRomperConciliacion = (conciliacion) => {
    setConciliacionARomper(conciliacion);
    setAlertRomperOpen(true);
  };
  
  // Confirmar romper conciliación
  const confirmarRomperConciliacion = async () => {
    if (!conciliacionARomper) return;
    
    try {
      await api.post(`/conciliacion/${conciliacionARomper.id}/romper`);
      toast.success('Conciliación revertida correctamente');
      setAlertRomperOpen(false);
      setConciliacionARomper(null);
      cargarDatos();
    } catch (error) {
      toast.error('Error al romper conciliación: ' + (error.response?.data?.detail || error.message));
    }
  };

  // Ordenar historial
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Historial ordenado
  const historialOrdenado = [...historialConciliados].sort((a, b) => {
    let valA = a[sortColumn];
    let valB = b[sortColumn];
    
    // Manejar valores nulos
    if (valA === null || valA === undefined) valA = '';
    if (valB === null || valB === undefined) valB = '';
    
    // Comparar números
    if (typeof valA === 'number' && typeof valB === 'number') {
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    }
    
    // Comparar strings (incluye fechas en formato ISO)
    const strA = String(valA).toLowerCase();
    const strB = String(valB).toLowerCase();
    if (sortDirection === 'asc') {
      return strA.localeCompare(strB);
    }
    return strB.localeCompare(strA);
  });

  // Manejar redimensionamiento de columna
  const handleColumnResize = (columnKey, startX, startWidth) => {
    const handleMouseMove = (e) => {
      const diff = e.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [columnKey]: newWidth }));
    };
    
    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Componente de header con ordenamiento y resize
  const SortableResizableHeader = ({ columnKey, label, sortKey, width, className = '' }) => (
    <TableHead 
      className={`relative select-none ${className}`}
      style={{ width: width, minWidth: width }}
    >
      <div 
        className="flex items-center gap-1 cursor-pointer hover:text-indigo-600"
        onClick={() => handleSort(sortKey)}
      >
        <span className="truncate">{label}</span>
        {sortColumn === sortKey && (
          <span className="text-indigo-600 font-bold">
            {sortDirection === 'asc' ? '↑' : '↓'}
          </span>
        )}
      </div>
      <div
        className="absolute right-0 top-0 h-full w-1 cursor-col-resize hover:bg-indigo-400 active:bg-indigo-600"
        onMouseDown={(e) => {
          e.preventDefault();
          handleColumnResize(columnKey, e.clientX, width);
        }}
      />
    </TableHead>
  );

  // Formatear monto
  const formatMonto = (monto) => {
    const num = parseFloat(monto) || 0;
    return num.toLocaleString('es-PE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const cuentaSeleccionada = cuentas.find(c => c.id.toString() === cuentaId);

  return (
    <div className="space-y-6" data-testid="conciliacion-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <ArrowRightLeft className="w-7 h-7 text-indigo-600" />
            Conciliación Bancaria
          </h1>
          <p className="text-gray-500 text-sm mt-1">Concilia los movimientos del banco con los registros del sistema</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setModalImportOpen(true)} data-testid="importar-btn">
            <Upload className="w-4 h-4 mr-2" />
            Importar Excel
          </Button>
          <Button onClick={handleConciliarAuto} className="bg-indigo-600 hover:bg-indigo-700" data-testid="auto-conciliar-btn">
            <RefreshCw className="w-4 h-4 mr-2" />
            Conciliar Auto
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="flex-1 min-w-[200px]">
              <Label>Cuenta Bancaria</Label>
              <Select value={cuentaId} onValueChange={setCuentaId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Seleccione cuenta" />
                </SelectTrigger>
                <SelectContent>
                  {cuentas.map(c => (
                    <SelectItem key={c.id} value={c.id.toString()}>
                      {c.nombre} ({c.moneda_codigo || 'PEN'})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Desde</Label>
              <Input type="date" value={fechaDesde} onChange={(e) => setFechaDesde(e.target.value)} className="mt-1" />
            </div>
            <div>
              <Label>Hasta</Label>
              <Input type="date" value={fechaHasta} onChange={(e) => setFechaHasta(e.target.value)} className="mt-1" />
            </div>
            <Button variant="outline" onClick={cargarDatos}>
              <Search className="w-4 h-4 mr-2" />
              Buscar
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Resumen */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-blue-50 to-white border-blue-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Mov. Banco Pendientes</p>
            <p className="text-2xl font-bold text-blue-600">{resumen.banco_count}</p>
            <p className="text-sm text-gray-400">S/. {formatMonto(resumen.banco_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-emerald-50 to-white border-emerald-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Mov. Sistema Pendientes</p>
            <p className="text-2xl font-bold text-emerald-600">{resumen.sistema_count}</p>
            <p className="text-sm text-gray-400">S/. {formatMonto(resumen.sistema_total)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-amber-50 to-white border-amber-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Diferencia Cantidad</p>
            <p className="text-2xl font-bold text-amber-600">{Math.abs(resumen.banco_count - resumen.sistema_count)}</p>
          </CardContent>
        </Card>
        <Card className="bg-gradient-to-br from-purple-50 to-white border-purple-200">
          <CardContent className="p-4">
            <p className="text-sm text-gray-500">Diferencia Monto</p>
            <p className="text-2xl font-bold text-purple-600">S/. {formatMonto(Math.abs(resumen.banco_total - resumen.sistema_total))}</p>
          </CardContent>
        </Card>
      </div>

      {/* Tabs: Pendientes / Historial */}
      <Tabs value={tabActiva} onValueChange={setTabActiva}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="pendientes">
            <AlertCircle className="w-4 h-4 mr-2" />
            Pendientes de Conciliar
          </TabsTrigger>
          <TabsTrigger value="banco">
            <Building2 className="w-4 h-4 mr-2" />
            Movimientos Banco
          </TabsTrigger>
          <TabsTrigger value="historial">
            <CheckCircle2 className="w-4 h-4 mr-2" />
            Historial Conciliados
          </TabsTrigger>
        </TabsList>

        {/* Tab Pendientes */}
        <TabsContent value="pendientes" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Movimientos del Banco */}
            <Card>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Building2 className="w-5 h-5 text-blue-600" />
                    Banco ({movBanco.length})
                  </CardTitle>
                  {movBancoMultiples.length > 0 && (
                    <Button size="sm" onClick={handleAbrirConciliarMultiples} className="bg-amber-600 hover:bg-amber-700">
                      <CheckCircle2 className="w-4 h-4 mr-1" />
                      Conciliar {movBancoMultiples.length} ({formatMonto(totalSeleccionados)})
                    </Button>
                  )}
                </div>
                {movBancoMultiples.length > 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    {movBancoMultiples.length} seleccionados | Total: S/. {formatMonto(totalSeleccionados)}
                  </p>
                )}
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-8"></TableHead>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Banco</TableHead>
                        <TableHead>Nro Op.</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movBanco.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                            No hay movimientos pendientes
                          </TableCell>
                        </TableRow>
                      ) : movBanco.map((mov) => {
                        const isSelected = movBancoMultiples.some(m => m.id === mov.id);
                        return (
                          <TableRow 
                            key={mov.id} 
                            className={`hover:bg-blue-50/50 cursor-pointer ${isSelected ? 'bg-amber-50' : ''}`}
                            onClick={() => toggleMovBancoSeleccionado(mov)}
                          >
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <input 
                                type="checkbox" 
                                checked={isSelected}
                                onChange={() => toggleMovBancoSeleccionado(mov)}
                                className="rounded border-gray-300"
                              />
                            </TableCell>
                            <TableCell className="text-sm">{mov.fecha}</TableCell>
                            <TableCell>
                              <Badge variant="outline" className="text-xs">{mov.banco || 'N/A'}</Badge>
                            </TableCell>
                            <TableCell className="text-xs font-mono text-gray-500">{mov.ref_banco || '-'}</TableCell>
                            <TableCell className="text-sm max-w-[120px] truncate" title={mov.descripcion}>
                              {mov.descripcion}
                            </TableCell>
                            <TableCell className={`text-right font-mono ${mov.monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {mov.monto_pen >= 0 ? '+' : ''}{formatMonto(mov.monto_pen)}
                            </TableCell>
                            <TableCell onClick={(e) => e.stopPropagation()}>
                              <Button variant="ghost" size="sm" onClick={() => handleAbrirConciliar(mov)}>
                                <Link2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* Movimientos del Sistema */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5 text-emerald-600" />
                  Sistema ({movSistema.length})
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="max-h-[500px] overflow-y-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                        <TableHead>Tipo</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {movSistema.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} className="text-center text-gray-400 py-8">
                            No hay movimientos pendientes
                          </TableCell>
                        </TableRow>
                      ) : movSistema.map((mov) => (
                        <TableRow key={mov.id} className="hover:bg-emerald-50/50">
                          <TableCell className="text-sm">{mov.fecha}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate" title={mov.descripcion}>
                            {mov.descripcion}
                          </TableCell>
                          <TableCell className={`text-right font-mono ${mov.monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {mov.monto_pen >= 0 ? '+' : ''}{formatMonto(mov.monto_pen)}
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{mov.tipo}</Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Tab Movimientos Banco - TODOS los movimientos */}
        <TabsContent value="banco" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Todos los Movimientos Importados ({movBancoTodos.length})</CardTitle>
                <div className="flex gap-2 text-sm">
                  <Badge className="bg-emerald-100 text-emerald-700">
                    Conciliados: {movBancoTodos.filter(m => m.conciliado).length}
                  </Badge>
                  <Badge className="bg-amber-100 text-amber-700">
                    Pendientes: {movBancoTodos.filter(m => !m.conciliado).length}
                  </Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Banco</TableHead>
                    <TableHead>Nro Operación</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Estado</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {movBancoTodos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} className="text-center text-gray-400 py-8">
                        No hay movimientos importados
                      </TableCell>
                    </TableRow>
                  ) : movBancoTodos.map((mov) => (
                    <TableRow key={mov.id} className={mov.conciliado ? 'bg-emerald-50/50' : ''}>
                      <TableCell className="text-sm">{mov.fecha}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs font-medium">{mov.banco || 'N/A'}</Badge>
                      </TableCell>
                      <TableCell className="text-sm font-mono text-gray-600">{mov.ref_banco || '-'}</TableCell>
                      <TableCell className="max-w-[300px] truncate text-sm">{mov.descripcion}</TableCell>
                      <TableCell className={`text-right font-mono font-medium ${mov.monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                        {mov.monto_pen >= 0 ? '+' : ''}{formatMonto(mov.monto_pen)}
                      </TableCell>
                      <TableCell>
                        {mov.conciliado ? (
                          <Badge className="bg-emerald-100 text-emerald-700"><CheckCircle2 className="w-3 h-3 mr-1" />Conciliado</Badge>
                        ) : (
                          <Badge className="bg-amber-100 text-amber-700"><AlertCircle className="w-3 h-3 mr-1" />Pendiente</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Tab Historial Conciliados */}
        <TabsContent value="historial" className="mt-4">
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">Historial de Conciliaciones</CardTitle>
                <p className="text-sm text-gray-500">
                  Click en columna para ordenar • Arrastre borde para redimensionar
                </p>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table style={{ tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }}>
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <SortableResizableHeader columnKey="fecha" label="Fecha" sortKey="fecha_mov" width={columnWidths.fecha} />
                      <SortableResizableHeader columnKey="banco" label="Banco" sortKey="banco" width={columnWidths.banco} />
                      <SortableResizableHeader columnKey="nro_op" label="Nro Op." sortKey="nro_op" width={columnWidths.nro_op} />
                      <SortableResizableHeader columnKey="desc_banco" label="Descripción Banco" sortKey="descripcion_banco" width={columnWidths.desc_banco} />
                      <SortableResizableHeader columnKey="monto_banco" label="Monto Banco" sortKey="monto_banco" width={columnWidths.monto_banco} className="text-right" />
                      <SortableResizableHeader columnKey="nro_doc" label="Nro Doc" sortKey="nro_doc" width={columnWidths.nro_doc} />
                      <SortableResizableHeader columnKey="proveedor" label="Proveedor/Beneficiario" sortKey="proveedor_beneficiario" width={columnWidths.proveedor} />
                      <SortableResizableHeader columnKey="detalle" label="Detalle Sistema" sortKey="detalle_sistema" width={columnWidths.detalle} />
                      <SortableResizableHeader columnKey="desc_sistema" label="Descripción Sistema" sortKey="descripcion_sistema" width={columnWidths.desc_sistema} />
                      <SortableResizableHeader columnKey="monto_sistema" label="Monto Sistema" sortKey="monto_sistema" width={columnWidths.monto_sistema} className="text-right" />
                      <SortableResizableHeader columnKey="estado" label="Estado" sortKey="estado" width={columnWidths.estado} />
                      <TableHead style={{ width: 50 }}></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historialOrdenado.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={12} className="text-center text-gray-400 py-8">
                          No hay conciliaciones realizadas
                        </TableCell>
                      </TableRow>
                    ) : historialOrdenado.map((c) => (
                      <TableRow key={c.id} className="bg-emerald-50/30 hover:bg-emerald-50/60">
                        <TableCell style={{ width: columnWidths.fecha }} className="text-sm font-medium">{c.fecha_mov}</TableCell>
                        <TableCell style={{ width: columnWidths.banco }}>
                          <Badge variant="outline" className="text-xs">{c.banco || 'N/A'}</Badge>
                        </TableCell>
                        <TableCell style={{ width: columnWidths.nro_op }} className="text-xs font-mono text-gray-600">{c.nro_op || '-'}</TableCell>
                        <TableCell style={{ width: columnWidths.desc_banco }} className="text-sm truncate" title={c.descripcion_banco}>
                          {c.descripcion_banco}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.monto_banco }} className={`text-right font-mono ${c.monto_banco >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {formatMonto(c.monto_banco)}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.nro_doc }} className="text-sm font-mono text-gray-600">
                          {c.nro_doc || '-'}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.proveedor }} className="text-sm truncate" title={c.proveedor_beneficiario}>
                          {c.proveedor_beneficiario || '-'}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.detalle }} className="text-sm text-gray-500 truncate">
                          {c.detalle_sistema || '-'}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.desc_sistema }} className="text-sm truncate" title={c.descripcion_sistema}>
                          {c.descripcion_sistema || <span className="text-gray-400">Sin vincular</span>}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.monto_sistema }} className="text-right font-mono">
                          {c.monto_sistema ? formatMonto(c.monto_sistema) : '-'}
                        </TableCell>
                        <TableCell style={{ width: columnWidths.estado }}>
                          <Badge className={
                            c.estado === 'OK' ? 'bg-emerald-100 text-emerald-700' :
                            c.estado === 'PARCIAL' ? 'bg-amber-100 text-amber-700' :
                            'bg-gray-100 text-gray-600'
                          }>{c.estado}</Badge>
                        </TableCell>
                        <TableCell style={{ width: 50 }}>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => handleRomperConciliacion(c)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                            title="Romper conciliación"
                          >
                            <Unlink className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Modal Importar Excel */}
      <Dialog open={modalImportOpen} onOpenChange={setModalImportOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Upload className="w-5 h-5 text-indigo-600" />
              Importar Estado de Cuenta
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Selección de cuenta */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Cuenta Bancaria *</Label>
                <Select value={cuentaId} onValueChange={setCuentaId}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Seleccione cuenta" />
                  </SelectTrigger>
                  <SelectContent>
                    {cuentas.map(c => (
                      <SelectItem key={c.id} value={c.id.toString()}>{c.nombre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Formato del Banco *</Label>
                <Select value={bancoSeleccionado} onValueChange={handleBancoChange}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BCP">BCP</SelectItem>
                    <SelectItem value="BBVA">BBVA Continental</SelectItem>
                    <SelectItem value="INTERBANK">Interbank</SelectItem>
                    <SelectItem value="SCOTIABANK">Scotiabank</SelectItem>
                    <SelectItem value="CUSTOM">Personalizado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Configuración de columnas */}
            {bancoSeleccionado === 'CUSTOM' && (
              <Card className="bg-gray-50">
                <CardContent className="p-4">
                  <p className="text-sm font-medium mb-3 flex items-center gap-2">
                    <Settings className="w-4 h-4" />
                    Configurar Columnas (usar letras: A, B, C...)
                  </p>
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Label className="text-xs">Filas a Saltar</Label>
                      <Input type="number" min="0" value={formatoConfig.skip_filas} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, skip_filas: parseInt(e.target.value) || 0})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Fecha</Label>
                      <Input value={formatoConfig.col_fecha} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_fecha: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Descripción</Label>
                      <Input value={formatoConfig.col_descripcion} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_descripcion: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Monto (único)</Label>
                      <Input value={formatoConfig.col_monto || ''} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_monto: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Cargo (-)</Label>
                      <Input value={formatoConfig.col_cargo || ''} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_cargo: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Abono (+)</Label>
                      <Input value={formatoConfig.col_abono || ''} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_abono: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Saldo</Label>
                      <Input value={formatoConfig.col_saldo || ''} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_saldo: e.target.value.toUpperCase()})} />
                    </div>
                    <div>
                      <Label className="text-xs">Col. Referencia</Label>
                      <Input value={formatoConfig.col_ref || ''} 
                        onChange={(e) => setFormatoConfig({...formatoConfig, col_ref: e.target.value.toUpperCase()})} />
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Subir archivo */}
            <div>
              <Label>Archivo Excel (.xlsx, .xls)</Label>
              <Input type="file" accept=".xlsx,.xls" onChange={handleFileUpload} className="mt-1" />
            </div>

            {/* Preview de datos crudos para debug */}
            {archivoData && archivoData.length > 0 && previewData.length === 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-sm font-medium text-amber-800 mb-2">⚠️ No se detectaron movimientos. Revisa las columnas del archivo:</p>
                <div className="text-xs font-mono bg-white p-2 rounded border overflow-x-auto">
                  <p className="mb-1"><strong>Fila 1:</strong> {JSON.stringify(archivoData[0])}</p>
                  {archivoData[1] && <p><strong>Fila 2:</strong> {JSON.stringify(archivoData[1])}</p>}
                </div>
                <p className="text-xs text-amber-600 mt-2">Prueba cambiar el formato a "Personalizado" y ajusta las columnas según tu archivo.</p>
              </div>
            )}

            {/* Preview */}
            {previewData.length > 0 && (
              <div>
                <p className="text-sm font-medium mb-2">Vista Previa ({previewData.length} movimientos)</p>
                <div className="max-h-[300px] overflow-y-auto border rounded-lg">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Fecha</TableHead>
                        <TableHead>Banco</TableHead>
                        <TableHead>Nro Op.</TableHead>
                        <TableHead>Descripción</TableHead>
                        <TableHead className="text-right">Monto</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {previewData.slice(0, 30).map((row, idx) => (
                        <TableRow key={idx}>
                          <TableCell className="text-sm">{row.fecha}</TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs">{bancoSeleccionado}</Badge>
                          </TableCell>
                          <TableCell className="text-sm font-mono text-gray-500">{row.ref || '-'}</TableCell>
                          <TableCell className="text-sm max-w-[200px] truncate" title={row.descripcion}>{row.descripcion}</TableCell>
                          <TableCell className={`text-right font-mono font-medium ${row.monto >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                            {row.monto >= 0 ? '+' : ''}{formatMonto(row.monto)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
                {previewData.length > 30 && (
                  <p className="text-xs text-gray-400 mt-1">Mostrando 30 de {previewData.length} movimientos</p>
                )}
              </div>
            )}
          </div>

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalImportOpen(false)}>Cancelar</Button>
            <Button onClick={handleImportar} disabled={importing || previewData.length === 0} className="bg-indigo-600 hover:bg-indigo-700">
              {importing ? 'Importando...' : `Importar ${previewData.length} Movimientos`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal Conciliar */}
      <Dialog open={modalConciliarOpen} onOpenChange={setModalConciliarOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="w-5 h-5 text-indigo-600" />
              Conciliar {movBancoMultiples.length > 1 ? `${movBancoMultiples.length} Movimientos` : 'Movimiento'}
            </DialogTitle>
          </DialogHeader>

          {movBancoMultiples.length > 0 && (
            <div className="space-y-4 mt-4">
              {/* Movimientos del banco seleccionados */}
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-4">
                  <p className="text-sm font-medium text-blue-800 mb-2">
                    {movBancoMultiples.length > 1 ? `${movBancoMultiples.length} Movimientos Seleccionados` : 'Movimiento Banco'}
                  </p>
                  {movBancoMultiples.length === 1 ? (
                    <div className="grid grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-gray-500">Fecha:</span>
                        <span className="ml-2 font-medium">{movBancoMultiples[0].fecha}</span>
                      </div>
                      <div className="col-span-2">
                        <span className="text-gray-500">Descripción:</span>
                        <span className="ml-2">{movBancoMultiples[0].descripcion}</span>
                      </div>
                      <div>
                        <span className="text-gray-500">Monto:</span>
                        <span className={`ml-2 font-bold ${movBancoMultiples[0].monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          S/. {formatMonto(movBancoMultiples[0].monto_pen)}
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="max-h-[120px] overflow-y-auto">
                        {movBancoMultiples.map((mov, idx) => (
                          <div key={mov.id} className="flex justify-between text-sm py-1 border-b border-blue-100 last:border-0">
                            <span className="text-gray-600">{mov.fecha} - {mov.descripcion?.substring(0, 30)}...</span>
                            <span className={`font-mono ${mov.monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                              {formatMonto(mov.monto_pen)}
                            </span>
                          </div>
                        ))}
                      </div>
                      <div className="flex justify-between pt-2 border-t border-blue-200 font-bold">
                        <span>TOTAL ({movBancoMultiples.length} mov.)</span>
                        <span className={totalSeleccionados >= 0 ? 'text-emerald-700' : 'text-red-700'}>
                          S/. {formatMonto(totalSeleccionados)}
                        </span>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Seleccionar pagos del sistema - solo si es 1 movimiento */}
              {movBancoMultiples.length === 1 && (
                <div>
                  <p className="text-sm font-medium mb-2">Seleccionar Movimientos del Sistema para Vincular</p>
                  <div className="max-h-[150px] overflow-y-auto border rounded-lg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-10"></TableHead>
                          <TableHead>Fecha</TableHead>
                          <TableHead>Descripción</TableHead>
                          <TableHead className="text-right">Monto</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {/* Filtrar movimientos según el signo del monto bancario */}
                        {(() => {
                          const montoBanco = movBancoMultiples[0]?.monto_pen || 0;
                          const movFiltrados = movSistema.filter(mov => {
                            // Si monto banco es negativo (egreso), mostrar solo egresos del sistema (negativos)
                            // Si monto banco es positivo (ingreso), mostrar solo ingresos del sistema (positivos)
                            if (montoBanco < 0) {
                              return mov.monto_pen < 0;
                            } else {
                              return mov.monto_pen >= 0;
                            }
                          });
                          
                          if (movFiltrados.length === 0) {
                            return (
                              <TableRow>
                                <TableCell colSpan={4} className="text-center text-gray-400 py-4">
                                  No hay {montoBanco < 0 ? 'egresos' : 'ingresos'} del sistema pendientes
                                </TableCell>
                              </TableRow>
                            );
                          }
                          
                          return movFiltrados.map((mov) => (
                            <TableRow 
                              key={mov.id} 
                              className={`cursor-pointer ${pagosSeleccionados.includes(mov.id) ? 'bg-emerald-50' : 'hover:bg-gray-50'}`}
                              onClick={() => togglePagoSeleccionado(mov.id)}
                            >
                              <TableCell>
                                <input 
                                  type="checkbox" 
                                  checked={pagosSeleccionados.includes(mov.id)} 
                                  onChange={() => togglePagoSeleccionado(mov.id)}
                                  className="rounded"
                                />
                              </TableCell>
                              <TableCell className="text-sm">{mov.fecha}</TableCell>
                              <TableCell className="text-sm max-w-[200px] truncate">{mov.descripcion}</TableCell>
                              <TableCell className={`text-right font-mono ${mov.monto_pen >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                                {formatMonto(mov.monto_pen)}
                              </TableCell>
                            </TableRow>
                          ));
                        })()}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}

              {/* Opciones cuando no hay contrapartida */}
              {pagosSeleccionados.length === 0 && (
                <Card className="bg-amber-50 border-amber-200">
                  <CardContent className="p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-amber-800">¿Crear gasto en sistema?</p>
                        <p className="text-xs text-amber-600">Se registrará como egreso en tesorería</p>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input 
                          type="checkbox" 
                          checked={crearGasto} 
                          onChange={(e) => setCrearGasto(e.target.checked)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-amber-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                      </label>
                    </div>

                    {crearGasto && (
                      <div className="space-y-3 pt-2 border-t border-amber-200">
                        <div>
                          <Label className="text-amber-800">Categoría de Gasto *</Label>
                          <Select value={categoriaGasto} onValueChange={setCategoriaGasto}>
                            <SelectTrigger className="mt-1 bg-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="ITF">ITF - Impuesto Transacciones Financieras</SelectItem>
                              <SelectItem value="COMISION">Comisión Bancaria</SelectItem>
                              <SelectItem value="MANTENIMIENTO">Mantenimiento de Cuenta</SelectItem>
                              <SelectItem value="PORTES">Portes Bancarios</SelectItem>
                              <SelectItem value="OTRO">Otro Gasto Bancario</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        {categoriaGasto === 'OTRO' && (
                          <div>
                            <Label className="text-amber-800">Descripción del Gasto</Label>
                            <Input 
                              value={descripcionGasto}
                              onChange={(e) => setDescripcionGasto(e.target.value)}
                              placeholder="Ej: Chequera, Certificación, etc."
                              className="mt-1 bg-white"
                            />
                          </div>
                        )}

                        <div>
                          <Label className="text-amber-800">Centro de Costo (opcional)</Label>
                          <Select value={centroCostoGasto} onValueChange={setCentroCostoGasto}>
                            <SelectTrigger className="mt-1 bg-white">
                              <SelectValue placeholder="Sin asignar" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none">Sin asignar</SelectItem>
                              {centrosCosto.map(cc => (
                                <SelectItem key={cc.id} value={cc.id.toString()}>{cc.nombre}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Nota */}
              <div>
                <Label>Nota (opcional)</Label>
                <Input 
                  value={notaConciliacion} 
                  onChange={(e) => setNotaConciliacion(e.target.value)}
                  placeholder="Agregar nota o comentario..."
                  className="mt-1"
                />
              </div>

              {/* Resumen */}
              {pagosSeleccionados.length > 0 && (
                <Card className="bg-emerald-50 border-emerald-200">
                  <CardContent className="p-3">
                    <p className="text-sm text-emerald-700">
                      <CheckCircle2 className="w-4 h-4 inline mr-1" />
                      Se vincularán {pagosSeleccionados.length} movimiento(s) del sistema
                    </p>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          <DialogFooter className="mt-6">
            <Button variant="outline" onClick={() => setModalConciliarOpen(false)}>Cancelar</Button>
            <Button onClick={handleConciliar} className="bg-indigo-600 hover:bg-indigo-700">
              {pagosSeleccionados.length === 0 ? 'Marcar Conciliado' : 'Conciliar Seleccionados'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AlertDialog para confirmar romper conciliación */}
      <AlertDialog open={alertRomperOpen} onOpenChange={setAlertRomperOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Unlink className="w-5 h-5" />
              ¿Romper esta conciliación?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-2">
              <p>Esta acción revertirá la conciliación y el movimiento bancario volverá a estar pendiente.</p>
              {conciliacionARomper && (
                <div className="bg-gray-50 p-3 rounded-lg mt-3 text-sm">
                  <p><strong>Fecha:</strong> {conciliacionARomper.fecha_mov}</p>
                  <p><strong>Banco:</strong> {conciliacionARomper.banco}</p>
                  <p><strong>Descripción:</strong> {conciliacionARomper.descripcion_banco}</p>
                  <p><strong>Monto:</strong> S/. {formatMonto(conciliacionARomper.monto_banco)}</p>
                </div>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConciliacionARomper(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmarRomperConciliacion}
              className="bg-red-600 hover:bg-red-700"
            >
              Sí, romper conciliación
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* AlertDialog para confirmar diferencia de montos */}
      <AlertDialog open={confirmacionDiferencia !== null} onOpenChange={(open) => !open && setConfirmacionDiferencia(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="w-5 h-5" />
              Los montos no coinciden
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>Existe una diferencia entre el monto del banco y el del sistema:</p>
              <div className="bg-gray-50 p-3 rounded-lg space-y-2 font-mono text-sm">
                <div className="flex justify-between">
                  <span>Monto Banco:</span>
                  <span className={confirmacionDiferencia?.monto_banco < 0 ? 'text-red-600' : 'text-green-600'}>
                    S/. {formatMonto(confirmacionDiferencia?.monto_banco || 0)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Monto Sistema:</span>
                  <span className={confirmacionDiferencia?.monto_sistema < 0 ? 'text-red-600' : 'text-green-600'}>
                    S/. {formatMonto(confirmacionDiferencia?.monto_sistema || 0)}
                  </span>
                </div>
                <div className="border-t pt-2 flex justify-between font-bold">
                  <span>Diferencia:</span>
                  <span className="text-amber-600">S/. {formatMonto(confirmacionDiferencia?.diferencia || 0)}</span>
                </div>
              </div>
              <p className="text-amber-700 text-sm font-medium">¿Desea conciliar de todas formas? La diferencia quedará registrada.</p>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setConfirmacionDiferencia(null)}>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={() => handleConciliar(true)}
              className="bg-amber-600 hover:bg-amber-700"
            >
              Sí, conciliar con diferencia
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ConciliacionPage;
